<template>
  <span v-if="temPermissao">
    <!-- Ativador: botão discreto pra ficha do paciente -->
    <v-btn small outlined color="primary" @click="abrir">
      <v-icon left small>mdi-share-variant</v-icon>
      Compartilhar prontuário
    </v-btn>

    <v-dialog v-model="dialog" max-width="760" persistent scrollable>
      <v-card>
        <v-toolbar flat dense color="primary" dark>
          <v-icon left>mdi-share-variant</v-icon>
          <v-toolbar-title>Compartilhar prontuário (link seguro)</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="fechar">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <!-- Geração -->
          <div class="text-subtitle-2 mb-2">Novo link</div>
          <v-row dense>
            <v-col cols="12" md="7">
              <v-text-field
                v-model="finalidade"
                label="Finalidade (LGPD)"
                placeholder="Ex.: 2ª opinião com Dr. Fulano"
                hint="Fica registrado na auditoria de acessos do paciente."
                persistent-hint
                maxlength="500"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="validadeHoras"
                label="Validade"
                :items="opcoesValidade"
                item-text="texto"
                item-value="horas"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="2" class="d-flex align-start">
              <v-btn color="primary" block :loading="gerando" @click="gerar">
                <v-icon left small>mdi-link-plus</v-icon>
                Gerar
              </v-btn>
            </v-col>
          </v-row>

          <!-- Link recém-gerado: exibido UMA única vez (só o hash fica no servidor) -->
          <v-alert v-if="linkGerado" type="success" text border="left" class="mt-2">
            <div class="text-body-2 font-weight-medium mb-1">
              Link gerado — copie agora: ele não poderá ser exibido novamente.
            </div>
            <div class="d-flex align-center">
              <code class="link-gerado flex-grow-1">{{ linkGerado.url }}</code>
              <v-btn small text color="primary" class="ml-2" @click="copiarLink">
                <v-icon left small>mdi-content-copy</v-icon>
                {{ copiado ? 'Copiado!' : 'Copiar' }}
              </v-btn>
            </div>
            <div class="text-caption mt-1">
              Expira em {{ formatarData(linkGerado.expiraEm) }}.
            </div>

            <!-- Envio opcional por WhatsApp -->
            <v-row dense class="mt-2" align="center">
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="telefoneDestino"
                  label="WhatsApp de destino (vazio = paciente)"
                  placeholder="(31) 99999-8888"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-btn small color="success" :loading="enviandoWhatsApp" @click="enviarWhatsApp(linkGerado.id)">
                  <v-icon left small>mdi-whatsapp</v-icon>
                  Enviar por WhatsApp
                </v-btn>
              </v-col>
              <v-col cols="12" class="text-caption grey--text text--darken-1 pt-0">
                Atenção: ao enviar por WhatsApp o link é reemitido por segurança — a cópia
                feita acima deixa de valer; passa a valer o link entregue na mensagem.
              </v-col>
            </v-row>
          </v-alert>

          <v-divider class="my-4"></v-divider>

          <!-- Listagem / revogação -->
          <div class="text-subtitle-2 mb-2">Links deste paciente</div>
          <v-data-table
            :headers="headers"
            :items="itens"
            :loading="carregando"
            dense
            disable-sort
            :footer-props="{ 'items-per-page-options': [5, 10, 25] }"
            no-data-text="Nenhum link de compartilhamento gerado para este paciente."
            loading-text="Carregando links..."
          >
            <template v-slot:[`item.criadoEm`]="{ item }">
              {{ formatarData(item.criadoEm) }}
            </template>
            <template v-slot:[`item.expiraEm`]="{ item }">
              {{ formatarData(item.expiraEm) }}
            </template>
            <template v-slot:[`item.status`]="{ item }">
              <v-chip small :color="corStatus(item)" text-color="white">
                {{ descreverStatus(item) }}
              </v-chip>
            </template>
            <template v-slot:[`item.acessos`]="{ item }">
              {{ item.acessosCount }}
              <span v-if="item.ultimoAcessoEm" class="text-caption grey--text">
                (último: {{ formatarData(item.ultimoAcessoEm) }})
              </span>
            </template>
            <template v-slot:[`item.acoes`]="{ item }">
              <v-btn
                v-if="item.ativo"
                x-small
                outlined
                color="error"
                :loading="revogandoId === item.id"
                @click="revogar(item.id)"
              >
                <v-icon left x-small>mdi-link-off</v-icon>
                Revogar
              </v-btn>
            </template>
          </v-data-table>

          <div class="text-caption grey--text mt-2">
            <v-icon x-small class="mr-1">mdi-shield-lock-outline</v-icon>
            O link dá acesso somente a um RESUMO do prontuário (dados básicos, alergias,
            medicações ativas e últimos 5 atendimentos). Cada acesso fica registrado na
            auditoria LGPD do paciente, com IP de origem.
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </span>
</template>

<script lang="ts">
import Vue from 'vue';
import PermissoesPadrao from '@/constants/enums/PermissoesPadrao';
import { CompartilhamentoService, CompartilhamentoGerado, CompartilhamentoItem } from '@/services/CompartilhamentoService';
import { MensagemMixin } from '@/helpers/MensagemHelper';
import { decode, getToken } from '@/utils/Token';

const service = new CompartilhamentoService();

// C8 — Dialog de compartilhamento de prontuário via link expirável/revogável.
// Standalone: basta soltar <soi-compartilhar-prontuario :paciente-id="..."/> na ficha do
// paciente. Gating pelo claim de permissions do JWT (padrão DashboardNps).
export default Vue.extend({
  name: 'soi-compartilhar-prontuario',
  props: {
    pacienteId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      temPermissao: false,
      dialog: false,
      finalidade: '' as string,
      validadeHoras: 72,
      opcoesValidade: [
        { horas: 24, texto: '24 horas' },
        { horas: 48, texto: '48 horas' },
        { horas: 72, texto: '72 horas (padrão)' },
        { horas: 168, texto: '7 dias (máximo)' },
      ],
      gerando: false,
      linkGerado: null as CompartilhamentoGerado | null,
      copiado: false,
      telefoneDestino: '' as string,
      enviandoWhatsApp: false,
      itens: [] as CompartilhamentoItem[],
      carregando: false,
      revogandoId: null as number | null,
      headers: [
        { text: 'Criado em', value: 'criadoEm' },
        { text: 'Finalidade', value: 'finalidade' },
        { text: 'Expira em', value: 'expiraEm' },
        { text: 'Status', value: 'status' },
        { text: 'Acessos', value: 'acessos' },
        { text: '', value: 'acoes', align: 'end' },
      ],
    };
  },
  created() {
    // Mesmo padrão de gating dos demais componentes (claim de permissions do JWT).
    const sessaoUsuario = decode(getToken());
    this.temPermissao = !!sessaoUsuario?.permissions?.includes(
      PermissoesPadrao.CompartilharProntuarioGerenciar,
    );
  },
  methods: {
    abrir() {
      this.dialog = true;
      this.linkGerado = null;
      this.copiado = false;
      this.carregarLista();
    },

    fechar() {
      this.dialog = false;
    },

    async carregarLista() {
      this.carregando = true;
      try {
        const res = await service.ListarPorPaciente(this.pacienteId);
        this.itens = res.data || [];
      } catch (err) {
        MensagemMixin(3000, 'error', 'Não foi possível carregar os links de compartilhamento.');
      } finally {
        this.carregando = false;
      }
    },

    async gerar() {
      this.gerando = true;
      this.copiado = false;
      try {
        const res = await service.Gerar(
          this.pacienteId,
          this.finalidade || undefined,
          this.validadeHoras,
        );
        this.linkGerado = res.data;
        await this.carregarLista();
      } catch (e: any) {
        const mensagem = typeof e?.response?.data === 'string' ? e.response.data : '';
        MensagemMixin(3000, 'error', mensagem || 'Não foi possível gerar o link.');
      } finally {
        this.gerando = false;
      }
    },

    async copiarLink() {
      if (!this.linkGerado) return;
      try {
        await navigator.clipboard.writeText(this.linkGerado.url);
        this.copiado = true;
      } catch (err) {
        // Fallback de clipboard (http/dev): seleção manual via prompt nativo.
        window.prompt('Copie o link abaixo:', this.linkGerado.url);
      }
    },

    async enviarWhatsApp(id: number) {
      this.enviandoWhatsApp = true;
      try {
        const res = await service.EnviarWhatsApp(id, this.telefoneDestino || undefined);
        const status = res.data?.status;
        if (status === 1) {
          MensagemMixin(3000, 'success', 'Link enviado por WhatsApp.');
          // O token foi rotacionado no envio: a URL exibida não vale mais.
          this.linkGerado = null;
        } else {
          MensagemMixin(4000, 'warning', res.data?.detalhes || 'O envio não foi concluído.');
        }
        await this.carregarLista();
      } catch (e: any) {
        const mensagem = typeof e?.response?.data === 'string' ? e.response.data : '';
        MensagemMixin(3000, 'error', mensagem || 'Não foi possível enviar por WhatsApp.');
      } finally {
        this.enviandoWhatsApp = false;
      }
    },

    async revogar(id: number) {
      this.revogandoId = id;
      try {
        await service.Revogar(id);
        MensagemMixin(3000, 'success', 'Link revogado.');
        if (this.linkGerado && this.linkGerado.id === id) {
          this.linkGerado = null;
        }
        await this.carregarLista();
      } catch (err) {
        MensagemMixin(3000, 'error', 'Não foi possível revogar o link.');
      } finally {
        this.revogandoId = null;
      }
    },

    descreverStatus(item: CompartilhamentoItem): string {
      if (item.revogadoEm) return 'Revogado';
      if (!item.ativo) return 'Expirado';
      return 'Ativo';
    },

    corStatus(item: CompartilhamentoItem): string {
      if (item.revogadoEm) return 'error';
      if (!item.ativo) return 'grey';
      return 'success';
    },

    formatarData(data: string): string {
      if (!data) return '';
      const d = new Date(data);
      return Number.isNaN(d.getTime()) ? data : d.toLocaleString('pt-BR');
    },
  },
});
</script>

<style scoped>
.link-gerado {
  word-break: break-all;
  font-size: 12px;
  padding: 4px 8px;
}
</style>
